import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import DriverTrackingMap from '../components/DriverTrackingMap';
import CrossSellRecommendations from '../components/CrossSellRecommendations';
import RatingDialog from '../components/RatingDialog';
import { saveActiveOrder, clearActiveOrder } from '../utils/activeOrder';
import { 
  CheckCircle, Clock, ChefHat, Package, Truck, 
  MapPin, Phone, ArrowLeft, Waves, CreditCard, Banknote, X
} from 'lucide-react';
import WegooLoader from '../components/WegooLoader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrderTrackingPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const orderType = searchParams.get('type') || 'food';
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRating, setShowRating] = useState(true);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Compute the cancellation penalty preview based on current status
  const cancellationPreview = () => {
    if (!order) return { amount: 0, reason: 'free' };
    const s = order.status;
    if (s === 'received' || s === 'pending_payment') return { amount: 0, reason: 'free' };
    if (s === 'preparing' || s === 'ready') {
      return { amount: Number(order.subtotal || 0) * 0.5, reason: 'half_food' };
    }
    if (s === 'out_for_delivery') return { amount: Number(order.total || 0), reason: 'total' };
    return { amount: 0, reason: 'not_allowed' };
  };

  const canCancel = order && !['delivered', 'cancelled'].includes(order.status);

  const handleCancel = async () => {
    if (!order) return;
    setCancelling(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/api/orders/${order.order_id}/cancel`, {
        method: 'POST',
        headers,
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        clearActiveOrder();
        toast.success(
          data.penalty > 0
            ? (language === 'pt' ? `Pedido cancelado. Penalidade: €${data.penalty.toFixed(2)}` : `Order cancelled. Penalty: €${data.penalty.toFixed(2)}`)
            : (language === 'pt' ? 'Pedido cancelado com sucesso' : 'Order cancelled successfully')
        );
        setOrder(prev => prev ? { ...prev, status: 'cancelled', cancellation_penalty: data.penalty } : prev);
        setCancelOpen(false);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || (language === 'pt' ? `Erro ao cancelar (${res.status})` : `Cancellation failed (${res.status})`));
      }
    } catch (e) {
      toast.error(language === 'pt' ? `Erro de rede: ${e?.message || ''}` : `Network error: ${e?.message || ''}`);
    } finally {
      setCancelling(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    let pollTimer = null;
    let ws = null;

    const buildEndpoint = () => {
      if (orderType === 'shisha') return `${API_URL}/api/shisha-rentals/${id}`;
      if (orderType === 'ice') return `${API_URL}/api/ice-orders/${id}`;
      return `${API_URL}/api/orders/${id}`;
    };

    const fetchOrder = async (isInitial = false) => {
      try {
        let response = await fetch(buildEndpoint());
        if (!response.ok && orderType !== 'food') {
          response = await fetch(`${API_URL}/api/orders/${id}`);
        }
        if (response.ok) {
          const data = await response.json();
          if (cancelled) return;
          setOrder(data);
          // Persist as active order so user can navigate away and come back
          if (data?.status && !['delivered', 'cancelled'].includes(data.status)) {
            saveActiveOrder(id, orderType);
          } else if (['delivered', 'cancelled'].includes(data?.status)) {
            clearActiveOrder();
          }
        }
      } catch (error) {
        console.error('Error fetching order:', error);
      } finally {
        if (isInitial && !cancelled) setLoading(false);
      }
    };

    // Initial fetch
    fetchOrder(true);

    // Polling fallback every 5s — guarantees real-time even if WS drops
    pollTimer = setInterval(() => fetchOrder(false), 5000);

    // WebSocket: instant updates when alive
    try {
      const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      ws = new WebSocket(`${wsUrl}/api/ws/order_${id}`);
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'status_update') {
            setOrder(prev => prev ? { ...prev, status: data.status } : prev);
            if (['delivered', 'cancelled'].includes(data.status)) {
              clearActiveOrder();
            }
          }
        } catch (e) { console.debug('ws message parse failed', e); }
      };
    } catch (e) { console.debug('ws connect failed; polling will cover', e); }

    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
      if (ws) try { ws.close(); } catch (e) { console.debug('ws close failed', e); }
    };
  }, [id, orderType]);

  const statusSteps = [
    { key: 'received', label: t('orderReceived'), icon: CheckCircle },
    { key: 'preparing', label: t('preparing'), icon: ChefHat },
    { key: 'ready', label: t('readyForPickup'), icon: Package },
    { key: 'out_for_delivery', label: t('outForDelivery'), icon: Truck },
    { key: 'delivered', label: t('delivered'), icon: CheckCircle },
  ];

  const getCurrentStepIndex = () => {
    if (!order) return 0;
    return statusSteps.findIndex(step => step.key === order.status);
  };

  if (loading) {
    return (
      <WegooLoader
        fullscreen
        label={language === 'pt' ? 'A preparar o teu pedido...' : 'Preparing your order...'}
      />
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#004E64] mb-4">
            {language === 'pt' ? 'Pedido não encontrado' : 'Order not found'}
          </h2>
          <Link to="/orders">
            <Button className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full">
              {language === 'pt' ? 'Ver Meus Pedidos' : 'View My Orders'}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const currentStepIndex = getCurrentStepIndex();

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-8">
      <div className="container mx-auto px-4 max-w-2xl">
        <Link to="/orders">
          <Button variant="ghost" className="mb-6 text-[#004E64]">
            <ArrowLeft size={20} className="mr-2" />
            {language === 'pt' ? 'Voltar aos Pedidos' : 'Back to Orders'}
          </Button>
        </Link>

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#004E64] mb-2" data-testid="order-tracking-title">
            {language === 'pt' ? 'Acompanhar Pedido' : 'Track Order'}
          </h1>
          <p className="text-[#9FA2B4]">#{order.order_id}</p>
          {order.is_beach_mode && (
            <span className="badge-beach inline-flex items-center gap-1 mt-2">
              <Waves size={14} />
              Beach Mode
            </span>
          )}
        </div>

        {/* Status Timeline */}
        <Card className="rounded-3xl border-[#004E64]/5 mb-6">
          <CardContent className="p-6">
            <div className="relative">
              {statusSteps.map((step, index) => {
                const isCompleted = index <= currentStepIndex;
                const isCurrent = index === currentStepIndex;
                const Icon = step.icon;

                return (
                  <div key={step.key} className="flex items-start gap-4 mb-6 last:mb-0">
                    <div className="relative">
                      <div 
                        className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                          isCompleted 
                            ? 'bg-[#2EC4B6] text-white' 
                            : 'bg-[#F4F1EA] text-[#9FA2B4]'
                        } ${isCurrent ? 'ring-4 ring-[#2EC4B6]/20' : ''}`}
                        data-testid={`status-${step.key}`}
                      >
                        <Icon size={20} />
                      </div>
                      {index < statusSteps.length - 1 && (
                        <div 
                          className={`absolute left-1/2 top-10 w-0.5 h-8 -translate-x-1/2 ${
                            index < currentStepIndex ? 'bg-[#2EC4B6]' : 'bg-[#F4F1EA]'
                          }`}
                        />
                      )}
                    </div>
                    <div className="flex-1 pt-2">
                      <p className={`font-semibold ${isCompleted ? 'text-[#004E64]' : 'text-[#9FA2B4]'}`}>
                        {step.label}
                      </p>
                      {isCurrent && (
                        <p className="text-sm text-[#FF6B35] animate-pulse">
                          {language === 'pt' ? 'Status atual' : 'Current status'}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Order Details */}
        <Card className="rounded-3xl border-[#004E64]/5 mb-6">
          <CardContent className="p-6">
            <h3 className="font-bold text-[#004E64] mb-4">
              {language === 'pt' ? 'Detalhes do Pedido' : 'Order Details'}
            </h3>
            
            <div className="space-y-3">
              {order.items.map((item, index) => (
                <div key={index} className="flex justify-between">
                  <span className="text-[#004E64]">{item.quantity}x {item.name}</span>
                  <span className="font-semibold text-[#004E64]">
                    €{(item.price * item.quantity).toFixed(2)}
                  </span>
                </div>
              ))}
              
              <hr className="border-[#004E64]/10" />
              
              <div className="flex justify-between text-[#9FA2B4]">
                <span>{t('subtotal')}</span>
                <span>€{order.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-[#9FA2B4]">
                <span>{t('deliveryFee')}</span>
                <span>€{order.delivery_fee.toFixed(2)}</span>
              </div>
              
              <hr className="border-[#004E64]/10" />
              
              <div className="flex justify-between text-lg">
                <span className="font-bold text-[#004E64]">{t('total')}</span>
                <span className="font-bold text-[#FF6B35]">€{order.total.toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Delivery Info */}
        <Card className="rounded-3xl border-[#004E64]/5 mb-6">
          <CardContent className="p-6">
            <h3 className="font-bold text-[#004E64] mb-4">
              {language === 'pt' ? 'Informações de Entrega' : 'Delivery Information'}
            </h3>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <MapPin className="text-[#FF6B35]" size={20} />
                <div>
                  <p className="text-[#004E64] font-medium">{order.delivery_location}</p>
                  {order.hotel_name && (
                    <p className="text-sm text-[#9FA2B4]">
                      {order.hotel_name} {order.room_number && `- Room ${order.room_number}`}
                    </p>
                  )}
                  {order.gps_coordinates && (
                    <p className="text-xs text-[#2EC4B6] mt-1">📍 GPS: {order.gps_coordinates}</p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Phone className="text-[#FF6B35]" size={20} />
                <p className="text-[#004E64]">{order.phone}</p>
              </div>

              {/* Payment Status */}
              <div className="flex items-center gap-3">
                {order.payment_method === 'cash' ? (
                  <Banknote className="text-yellow-500" size={20} />
                ) : (
                  <CreditCard className="text-[#FF6B35]" size={20} />
                )}
                <div>
                  <p className="text-[#004E64] font-medium">
                    {order.payment_method === 'cash' 
                      ? (language === 'pt' ? 'Pagamento na Entrega' : 'Cash on Delivery')
                      : order.payment_method === 'card' ? 'Cartão'
                      : order.payment_method === 'mbway' ? 'MBWay'
                      : order.payment_method === 'apple_pay' ? 'Apple Pay'
                      : order.payment_method}
                  </p>
                  <p className={`text-sm ${order.payment_status === 'paid' ? 'text-green-600' : 'text-yellow-600'}`}>
                    {order.payment_status === 'paid' 
                      ? (language === 'pt' ? '✓ Pago' : '✓ Paid')
                      : (language === 'pt' ? '⏳ Pagamento Pendente' : '⏳ Payment Pending')}
                  </p>
                </div>
              </div>

              {order.notes && (
                <div className="p-3 bg-[#F4F1EA] rounded-xl">
                  <p className="text-sm text-[#9FA2B4]">
                    <strong>{t('notes')}:</strong> {order.notes}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Driver Tracking Map - Shows when order is out for delivery */}
        {(order.status === 'out_for_delivery' || order.status === 'ready') && order.driver_id && (
          <DriverTrackingMap
            orderId={order.order_id}
            deliveryLocation={order.delivery_location}
            deliveryCoordinates={order.gps_coordinates}
            language={language}
          />
        )}

        {/* Rating Dialog - Shows when delivered and not yet rated */}
        {order.status === 'delivered' && !order.is_rated && showRating && token && (
          <RatingDialog
            orderId={order.order_id}
            token={token}
            language={language}
            onClose={() => setShowRating(false)}
            onSubmit={() => setOrder(prev => ({ ...prev, is_rated: true }))}
          />
        )}

        {/* Cross-sell Recommendations */}
        {order.items?.length > 0 && order.items[0]?.restaurant_id && (
          <CrossSellRecommendations
            restaurantId={order.items[0].restaurant_id}
            language={language}
          />
        )}

        {/* Cancel Order */}
        {canCancel && (
          <Card className="rounded-3xl border-red-100 mt-6">
            <CardContent className="p-6">
              <h3 className="font-bold text-red-700 mb-2">
                {language === 'pt' ? 'Cancelar Pedido' : 'Cancel Order'}
              </h3>
              <p className="text-xs text-[#9FA2B4] mb-3">
                {(() => {
                  const p = cancellationPreview();
                  if (p.reason === 'free') return language === 'pt' ? 'Pode cancelar gratuitamente.' : 'You can cancel for free.';
                  if (p.reason === 'half_food') return language === 'pt'
                    ? `Será cobrada uma penalidade de €${p.amount.toFixed(2)} (50% da comida).`
                    : `A penalty of €${p.amount.toFixed(2)} (50% of food) will be charged.`;
                  if (p.reason === 'total') return language === 'pt'
                    ? `Já saiu para entrega — será cobrado o total: €${p.amount.toFixed(2)}.`
                    : `Out for delivery — full total will be charged: €${p.amount.toFixed(2)}.`;
                  return '';
                })()}
              </p>
              <Button
                variant="outline"
                onClick={() => setCancelOpen(true)}
                className="border-red-300 text-red-600 hover:bg-red-50 rounded-full"
                data-testid="cancel-order-btn"
              >
                <X size={16} className="mr-1" />
                {language === 'pt' ? 'Cancelar pedido' : 'Cancel order'}
              </Button>
            </CardContent>
          </Card>
        )}

        <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
          <AlertDialogContent data-testid="cancel-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>
                {language === 'pt' ? 'Confirmar cancelamento' : 'Confirm cancellation'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {(() => {
                  const p = cancellationPreview();
                  if (p.reason === 'free') {
                    return language === 'pt'
                      ? 'Tem a certeza que quer cancelar este pedido? Não haverá qualquer cobrança.'
                      : 'Are you sure you want to cancel this order? You will not be charged.';
                  }
                  if (p.reason === 'half_food') {
                    return language === 'pt'
                      ? `O restaurante já começou a preparar o seu pedido. Se cancelar, será cobrada uma penalidade de €${p.amount.toFixed(2)} (50% do valor da comida). Deseja continuar?`
                      : `The restaurant has already started preparing your order. A €${p.amount.toFixed(2)} penalty (50% of food) will apply. Continue?`;
                  }
                  if (p.reason === 'total') {
                    return language === 'pt'
                      ? `O seu pedido já saiu para entrega. Se cancelar agora, será cobrado o valor TOTAL do pedido: €${p.amount.toFixed(2)}. Deseja continuar?`
                      : `Your order is already out for delivery. Cancelling now charges the FULL amount: €${p.amount.toFixed(2)}. Continue?`;
                  }
                  return '';
                })()}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-keep-btn">
                {language === 'pt' ? 'Manter pedido' : 'Keep order'}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleCancel}
                disabled={cancelling}
                className="bg-red-600 hover:bg-red-700"
                data-testid="cancel-confirm-btn"
              >
                {cancelling
                  ? (language === 'pt' ? 'A cancelar...' : 'Cancelling...')
                  : (language === 'pt' ? 'Confirmar cancelamento' : 'Confirm cancellation')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default OrderTrackingPage;
